import { createContext, useState, useEffect, ReactNode, useMemo } from "react";
import { useLocation, useNavigationType, NavigationType, useNavigate, To, useSearchParams } from "react-router-dom";
import qs from "qs";

interface NavigationHistory {
  key: string;
  pathname: string;
  search: string;
  hash: string;
  state: unknown;
}

type BackNavigate = (params?: { reserveLocation?: To; searchParamsOrString?: Record<string, string> | string }) => void;

interface NavigationHistoryContextType {
  history: NavigationHistory[];
  currentIndex: number;
  backNavigate: BackNavigate;
}

export const NavigationHistoryContext = createContext<NavigationHistoryContextType>({
  history: [],
  currentIndex: -1,
  backNavigate: () => {},
});

export const NavigationHistoryProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const navigationType = useNavigationType();
  const [history, setHistory] = useState<NavigationHistory[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(-1);

  useEffect(() => {
    setHistory((prevHistory) => {
      const newHistory = [...prevHistory];

      if (navigationType === NavigationType.Push) {
        const trimmedHistory = newHistory.slice(0, currentIndex + 1);
        trimmedHistory.push({
          key: location.key,
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
          state: location.state,
        });
        setCurrentIndex(trimmedHistory.length - 1);
        return trimmedHistory;
      }
      if (navigationType === NavigationType.Replace) {
        if (currentIndex !== -1 && newHistory[currentIndex]) {
          newHistory[currentIndex] = {
            key: location.key,
            pathname: location.pathname,
            search: location.search,
            hash: location.hash,
            state: location.state,
          };
        } else {
          newHistory.push({
            key: location.key,
            pathname: location.pathname,
            search: location.search,
            hash: location.hash,
            state: location.state,
          });
          setCurrentIndex(0);
        }
        return newHistory;
      }
      if (navigationType === NavigationType.Pop) {
        const existingIndex = newHistory.findIndex((loc) => loc.key === location.key);
        if (existingIndex !== -1) {
          setCurrentIndex(existingIndex);
        } else {
          newHistory.push({
            key: location.key,
            pathname: location.pathname,
            search: location.search,
            hash: location.hash,
            state: location.state,
          });
          setCurrentIndex(newHistory.length - 1);
        }
        return newHistory;
      }

      return newHistory;
    });
  }, [location, navigationType]);

  const backNavigate: BackNavigate = ({ reserveLocation, searchParamsOrString } = {}) => {
    const previousLocation = history[currentIndex - 1];

    if (history.length > 1) {
      navigate(-1);
      // TODO: сомнительно, но не нашел другого варианта добавить searchParams к урлу при navigate(-1);
      // TODO: имеется баг: если страница, на которую мы переходим тяжелая для отрисовки (долго отрисовывается),
      // то переданные параметры могут переписаться
      setTimeout(() => {
        const searchParams =
          typeof searchParamsOrString === "string" ? qs.parse(searchParamsOrString) : searchParamsOrString;

        setSearchParams(qs.stringify({ ...qs.parse(previousLocation.search.replace("?", "")), ...searchParams }), {
          replace: true,
        });
      });
    } else {
      navigate(reserveLocation || "", { replace: true });
    }
  };

  const providerValue = useMemo(
    () => ({
      history,
      currentIndex,
      backNavigate,
    }),
    [history, currentIndex, backNavigate]
  );

  return <NavigationHistoryContext.Provider value={providerValue}>{children}</NavigationHistoryContext.Provider>;
};
